/**
 * Auth Context — PKCE Authorization Code Flow
 *
 * Manages authentication state via Keycloak PKCE (no keycloak-js adapter).
 * Login: redirect to Keycloak → /auth/callback?code= → exchange → JWT.
 * Session: JWT stored in localStorage; user parsed from token claims.
 */

import { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { appendPkce } from '@/lib/pkce';
import { useMagicLink } from '@/hooks/useMagicLink';
import { apiClient, cancelAuthFailure, setAuthRefreshCallback, readStorage } from '@/lib/api';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface AuthUser {
  email?: string;
  name?: string;
  sub?: string;
  roles?: string[];
  emailVerified?: boolean;
}

interface AuthContextType {
  keycloak: null; // retained for interface compat with legacy consumers
  authenticated: boolean;
  loading: boolean;
  user: AuthUser | null;
  token?: string;
  /** gr_… API key — set on signup, read from localStorage on init. Use this instead of direct localStorage reads. */
  apiKey?: string;
  subscriptionSyncing: boolean;
  /** Redirect to Keycloak login (PKCE). Saves returnTo in sessionStorage. */
  login: (returnTo?: string) => void;
  logout: () => void;
  register: () => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  /** Store the gr_… API key in context + localStorage. Called after signup/onboarding. */
  setApiKey: (key: string) => void;
  // Magic-link stubs — MagicLinkPage compat
  magicLinkStatus: 'idle' | 'processing' | 'success' | 'error';
  magicLinkError?: string;
  magicLinkRedirect?: string;
  /** Verify a magic link token from the email URL (/login/magic/:token). */
  processMagicLink: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | undefined>();
  const [apiKey, setApiKeyState] = useState<string | undefined>(
    localStorage.getItem('guardrail_api_key') ?? undefined
  );
  const [subscriptionSyncing, setSubscriptionSyncing] = useState(false);
  const [magicLinkStatus, setMagicLinkStatus] = useState<
    'idle' | 'processing' | 'success' | 'error'
  >('idle');
  const [magicLinkError, setMagicLinkError] = useState<string | undefined>();
  const [magicLinkRedirect, setMagicLinkRedirect] = useState<string | undefined>();
  const initDone = useRef(false);
  const queryClient = useQueryClient();

  // === Helpers ===

  const clearAuthStorage = () => {
    localStorage.removeItem('guardrail_access_token');
    localStorage.removeItem('guardrail_refresh_token');
    localStorage.removeItem('guardrail_api_key');
    localStorage.removeItem('guardrail_user_email');
    setApiKeyState(undefined);
  };

  /** Decode JWT payload without verifying signature (trust is on KC's side). */
  const parseJwt = (raw: string): Record<string, unknown> => {
    try {
      const b64 = raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(b64));
    } catch {
      return {};
    }
  };

  const applyUser = (raw: string) => {
    const c = parseJwt(raw);
    setUser({
      email: c.email as string,
      name: ((c.name || c.preferred_username) as string) || undefined,
      sub: c.sub as string,
      roles: (c.realm_access as { roles?: string[] })?.roles ?? [],
      emailVerified: (c.email_verified as boolean) ?? true,
    });
  };

  // === Public API ===

  // Forward ref so setTokens can call refreshSession before it is declared,
  // avoiding a react-hooks/immutability temporal access violation.
  const refreshSessionRef = useRef<(() => Promise<boolean>) | null>(null);

  /**
   * Store JWT tokens, parse user claims, and mark authenticated.
   *
   * @param enableSync - When false, skips the post-login subscription sync.
   *   Pass false from refreshSession to prevent an infinite sync→refresh→sync loop.
   */
  const setTokens = (accessToken: string, refreshToken?: string, enableSync = true) => {
    localStorage.setItem('guardrail_access_token', accessToken);
    if (refreshToken) localStorage.setItem('guardrail_refresh_token', refreshToken);
    applyUser(accessToken);
    setToken(accessToken);
    setAuthenticated(true);
    // Cancel any in-flight deferred logout from a concurrent 401 — the fresh
    // token proves auth has recovered and the user should stay signed in.
    cancelAuthFailure();

    if (!enableSync) return;

    if (import.meta.env.DEV) console.log('[Auth] Tokens stored, user authenticated');

    // Standard login behavior: reconcile latest subscription entitlements, then
    // refresh the token so the new KC attributes are encoded in the next JWT.
    setSubscriptionSyncing(true);
    void apiClient
      .syncSubscription(accessToken)
      .then(async (result) => {
        if (result.synced) {
          if (import.meta.env.DEV)
            console.log('[Auth] Subscription entitlements reconciled on login', {
              subscriptions: result.subscriptions ?? 0,
            });
          // Refresh the access token so KC attributes updated by the sync are
          // encoded in a new JWT rather than persisting until natural expiry.
          await refreshSessionRef.current?.().catch((e) =>
            console.warn('[Auth] Post-sync token refresh failed (non-fatal):', e)
          );
        }
      })
      .catch((error) => {
        console.warn('[Auth] Subscription reconciliation failed on login', error);
      })
      .finally(() => {
        // Invalidate the userInfo cache so the next render from useUserInfo
        // fetches fresh data from /v2/me, which reads the just-synced KV state.
        void queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
        setSubscriptionSyncing(false);
      });
  };

  /** Store a gr_… API key in context and localStorage. */
  const setApiKey = (key: string) => {
    localStorage.setItem('guardrail_api_key', key);
    setApiKeyState(key);
  };

  /**
   * Initiate Keycloak PKCE login.
   * Stores returnTo in sessionStorage; AuthCallbackPage reads it after exchange.
   *
   * @param returnTo - path to navigate after successful login (default: /dashboard)
   */
  const login = (returnTo = '/dashboard') => {
    sessionStorage.setItem('login_redirect', returnTo);
    fetch(`${API_URL}/v2/auth/login-url`)
      .then((r) => {
        if (!r.ok) throw new Error(`login-url failed: ${r.status}`);
        return r.json() as Promise<{ loginUrl?: string }>;
      })
      .then(({ loginUrl }) => {
        if (!loginUrl) throw new Error('No loginUrl in response');
        return appendPkce(loginUrl);
      })
      .then((url) => {
        window.location.href = url;
      })
      .catch((err) => console.error('[Auth] login redirect failed:', err));
  };

  const logout = () => {
    clearAuthStorage();
    setAuthenticated(false);
    setUser(null);
    setToken(undefined);
    window.location.href = '/login';
  };

  const register = () => {
    window.location.href = '/signup';
  };

  // Wire useMagicLink hook — handles GET /v2/auth/magic-link/:token and calls setTokens on success
  const { verify: verifyMagicLinkToken } = useMagicLink(
    (accessToken, refreshToken) => setTokens(accessToken, refreshToken),
    setMagicLinkStatus,
    setMagicLinkError,
    setMagicLinkRedirect
  );

  const processMagicLink = (t: string) => {
    verifyMagicLinkToken(t);
  };

  const refreshSession = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('guardrail_refresh_token');
    if (!refreshToken) return false;

    try {
      // Proxy through the API to avoid direct browser→Keycloak calls.
      // This prevents CORS issues, network-visibility failures, and client-ID
      // mismatches between local (guardrail-web) and remote (tl4c-web) realms.
      const response = await fetch(`${API_URL}/v2/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        console.warn('[Auth] Refresh token exchange failed', { status: response.status });
        return false;
      }

      const tokenData = (await response.json()) as {
        access_token?: string;
        refresh_token?: string;
      };

      if (!tokenData.access_token) return false;
      // Pass enableSync=false — refreshSession is called after a sync completes;
      // re-triggering sync here would create an infinite sync→refresh loop.
      setTokens(tokenData.access_token, tokenData.refresh_token || refreshToken, false);
      if (import.meta.env.DEV) console.log('[Auth] Access token refreshed from refresh token');
      return true;
    } catch (error) {
      console.warn('[Auth] Refresh token flow failed', error);
      return false;
    }
  };
  // Wire ref after declaration so setTokens always calls the current version.
  // Must be in useLayoutEffect — writing .current directly in render body is not allowed.
  useLayoutEffect(() => {
    refreshSessionRef.current = refreshSession;
  });

  // === Init: restore JWT session from localStorage ===

  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    // Register refresh callback so api.ts can silently refresh on 401.
    // refreshSession is captured as a stale closure here ([] dep array) but all
    // values it closes over are stable: React state setters, module-level
    // constants, and queryClient from useQueryClient (guaranteed stable ref).
    setAuthRefreshCallback(refreshSession);
    const initializeSession = async () => {
      const stored = readStorage('guardrail_access_token');
      if (stored) {
        const claims = parseJwt(stored);
        const exp = claims.exp as number | undefined;
        if (!exp || exp * 1000 > Date.now()) {
          // Token present and not expired — restore session
          applyUser(stored);
          setToken(stored);
          setAuthenticated(true);
          if (import.meta.env.DEV) console.log('[Auth] Session restored from localStorage');
          // Sync subscription on restore to pick up any changes since last login
          // (e.g. webhook updated tier while the user was away).
          setSubscriptionSyncing(true);
          void apiClient
            .syncSubscription(stored)
            .then(async (result) => {
              if (result.synced) {
                if (import.meta.env.DEV)
                  console.log('[Auth] Subscription synced on session restore', {
                    subscriptions: result.subscriptions ?? 0,
                  });
                // Refresh the token so updated KC attributes reflect in the JWT.
                await refreshSessionRef.current?.().catch((e) =>
                  console.warn('[Auth] Post-restore token refresh failed (non-fatal):', e)
                );
              }
            })
            .catch((err) => {
              console.warn('[Auth] Subscription sync failed on session restore', err);
            })
            .finally(() => {
              void queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
              setSubscriptionSyncing(false);
            });
          setLoading(false);
          return;
        }

        // Access token expired — try refresh token before clearing session.
        const refreshed = await refreshSession();
        if (!refreshed) {
          clearAuthStorage();
          if (import.meta.env.DEV)
            console.log('[Auth] Stored token expired and refresh failed, cleared');
        }
        setLoading(false);
        return;
      }

      // No access token in storage — attempt refresh-only recovery.
      await refreshSession();
      setLoading(false);
    };

    void initializeSession();

    // Proactive refresh: check every 2 min; refresh if token expires within 5 min.
    // Access tokens are 30 min (KC realm setting). Refresh well before expiry to
    // avoid 401s on in-flight requests. The 401 interceptor in api.ts is a safety
    // net — proactive refresh should handle the common case.
    const REFRESH_CHECK_MS = 120_000;   // 2 minutes
    const REFRESH_THRESHOLD_MS = 300_000; // 5 minutes before expiry
    const refreshTimer = setInterval(() => {
      const stored = readStorage('guardrail_access_token');
      if (!stored) return;
      try {
        const b64 = stored.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(atob(b64)) as { exp?: number };
        if (exp && exp * 1000 - Date.now() < REFRESH_THRESHOLD_MS) {
          void refreshSession().then((ok) => {
            if (!ok)
              console.warn(
                '[Auth] Proactive refresh failed; user may be logged out on next API call.'
              );
          });
        }
      } catch {
        // malformed token — ignore, will be caught on next API call
      }
    }, REFRESH_CHECK_MS);

    return () => clearInterval(refreshTimer);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        keycloak: null,
        authenticated,
        loading,
        user,
        token,
        apiKey,
        subscriptionSyncing,
        login,
        logout,
        register,
        setTokens,
        setApiKey,
        magicLinkStatus,
        magicLinkError,
        magicLinkRedirect,
        processMagicLink,
      }}
    >
      {children}
      {/* Syncing state shown via tier badge in AppHeader — no separate toast needed */}
    </AuthContext.Provider>
  );
};
